import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { ArrowLeft, CreditCard, ShoppingCart, Globe, Wifi, AlertTriangle, Search, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Skeleton } from '../../components/ui/Skeleton';
import { useCard, useCardTransactions, useDisputeTransaction } from '../../hooks/useCards';
import type { CardTransactionStatus } from '../../types/cards';

const statusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'default' }> = {
  PENDING: { label: 'Pendente', variant: 'warning' },
  APPROVED: { label: 'Aprovada', variant: 'success' },
  DECLINED: { label: 'Recusada', variant: 'error' },
  SETTLED: { label: 'Liquidada', variant: 'success' },
  REVERSED: { label: 'Estornada', variant: 'default' },
  DISPUTED: { label: 'Em Disputa', variant: 'warning' },
};

export function CardTransactions() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showDisputeModal, setShowDisputeModal] = useState<string | null>(null);
  const [disputeReason, setDisputeReason] = useState('');

  const { data: card, isLoading: isLoadingCard } = useCard(id || '');
  const { data: transactionsData, isLoading: isLoadingTx } = useCardTransactions(id || '', {
    status: (statusFilter || undefined) as CardTransactionStatus | undefined,
  });
  const disputeMutation = useDisputeTransaction();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const handleDispute = async (transactionId: string) => {
    try {
      await disputeMutation.mutateAsync({
        transactionId,
        data: { reason: disputeReason },
      });
      setShowDisputeModal(null);
      setDisputeReason('');
    } catch (error) {
      console.error('Erro ao abrir disputa:', error);
    }
  };

  const filteredTransactions = transactionsData?.transactions?.filter((tx) => {
    if (!search) return true;
    return tx.merchantName.toLowerCase().includes(search.toLowerCase());
  }) || [];

  if (isLoadingCard) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="text-center py-12">
        <CreditCard className="w-12 h-12 text-surface-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-surface-100 mb-2">Cartão não encontrado</h2>
        <Button variant="secondary" onClick={() => navigate('/banking/cards')}>
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="secondary" size="sm" onClick={() => navigate(`/banking/cards/${id}`)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Transações do Cartão</h1>
          <p className="text-surface-400">{card.brand} •••• {card.panMasked.slice(-4)}</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                <Input
                  placeholder="Buscar por estabelecimento..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-48">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={[
                  { value: '', label: 'Todos os status' },
                  { value: 'PENDING', label: 'Pendente' },
                  { value: 'APPROVED', label: 'Aprovada' },
                  { value: 'DECLINED', label: 'Recusada' },
                  { value: 'SETTLED', label: 'Liquidada' },
                  { value: 'DISPUTED', label: 'Em Disputa' },
                ]}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Histórico de Transações
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingTx ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 text-surface-400 mx-auto mb-4" />
              <p className="text-surface-400">Nenhuma transação encontrada</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTransactions.map((tx) => {
                const status = statusConfig[tx.status] || { label: tx.status, variant: 'default' as const };
                const isDebit = ['PURCHASE', 'WITHDRAWAL', 'FEE'].includes(tx.type);

                return (
                  <div
                    key={tx.id}
                    className="p-4 bg-surface-800/50 rounded-lg hover:bg-surface-800 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${isDebit ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
                          {isDebit ? (
                            <ArrowUpRight className={`w-5 h-5 ${isDebit ? 'text-red-400' : 'text-green-400'}`} />
                          ) : (
                            <ArrowDownLeft className="w-5 h-5 text-green-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-surface-100 font-medium">{tx.merchantName}</p>
                          <p className="text-sm text-surface-400">{tx.merchantCategory}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-surface-500">
                              {new Date(tx.createdAt).toLocaleString('pt-BR')}
                            </span>
                            {tx.isOnline && (
                              <span title="Compra Online">
                                <ShoppingCart className="w-3 h-3 text-surface-500" />
                              </span>
                            )}
                            {tx.isInternational && (
                              <span title="Internacional">
                                <Globe className="w-3 h-3 text-surface-500" />
                              </span>
                            )}
                            {tx.isContactless && (
                              <span title="Aproximação">
                                <Wifi className="w-3 h-3 text-surface-500" />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${isDebit ? 'text-red-400' : 'text-green-400'}`}>
                          {isDebit ? '-' : '+'}{formatCurrency(tx.amount)}
                        </p>
                        <Badge variant={status.variant} className="mt-1">
                          {status.label}
                        </Badge>
                        {tx.installments && tx.installments > 1 && (
                          <p className="text-xs text-surface-400 mt-1">
                            {tx.installments}x de {formatCurrency(tx.installmentAmount || tx.amount / tx.installments)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    {tx.status === 'SETTLED' && tx.type === 'PURCHASE' && (
                      <div className="mt-3 pt-3 border-t border-surface-700">
                        {showDisputeModal === tx.id ? (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-yellow-400">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="text-sm font-medium">Abrir Disputa</span>
                            </div>
                            <textarea
                              className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 text-sm"
                              placeholder="Descreva o motivo da disputa..."
                              rows={2}
                              value={disputeReason}
                              onChange={(e) => setDisputeReason(e.target.value)}
                            />
                            <div className="flex gap-2">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                  setShowDisputeModal(null);
                                  setDisputeReason('');
                                }}
                              >
                                Cancelar
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleDispute(tx.id)}
                                disabled={!disputeReason || disputeMutation.isPending}
                              >
                                {disputeMutation.isPending ? 'Enviando...' : 'Enviar Disputa'}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowDisputeModal(tx.id)}
                          >
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            Contestar Transação
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
